import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { X, Plus } from 'lucide-react';

/**
 * Multi-entry invoice numbers. A dispatch challan can be billed through several
 * invoices (partial / split billing), so each number is kept as its own entry.
 */
export const InvoiceNumbersInput = ({ value = [], onChange, placeholder = 'Invoice number', testId = 'invoice-numbers' }) => {
  const [draft, setDraft] = useState('');

  const add = () => {
    const parts = draft.split(/[,;]/).map(p => p.trim()).filter(Boolean);
    if (!parts.length) return;
    const existing = new Set(value.map(v => v.toLowerCase()));
    const next = [...value];
    parts.forEach(p => { if (!existing.has(p.toLowerCase())) { existing.add(p.toLowerCase()); next.push(p); } });
    onChange(next);
    setDraft('');
  };

  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));

  return (
    <div className="space-y-1.5" data-testid={testId}>
      <div className="flex gap-1.5">
        <Input
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); } }}
          onBlur={add}
          placeholder={placeholder}
          className="h-8 text-xs font-mono"
          data-testid={`${testId}-input`}
        />
        <Button type="button" size="sm" variant="outline" className="h-8 px-2" onClick={add} disabled={!draft.trim()} data-testid={`${testId}-add`}>
          <Plus className="w-3.5 h-3.5" />
        </Button>
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((inv, i) => (
            <span key={`${inv}-${i}`} className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-slate-50 px-2 py-0.5 text-[11px] font-mono"
              data-testid={`${testId}-chip-${i}`}>
              {inv}
              <button type="button" onClick={() => remove(i)} className="text-slate-400 hover:text-red-600" data-testid={`${testId}-remove-${i}`}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <p className="text-[10px] text-slate-400">Press Enter or comma to add another invoice</p>
    </div>
  );
};

export const parseInvoiceNumbers = (challan) => {
  if (Array.isArray(challan?.invoice_numbers) && challan.invoice_numbers.length) return challan.invoice_numbers;
  return String(challan?.invoice_number || '').split(/[,;]/).map(p => p.trim()).filter(Boolean);
};
